// ============================================================================
// Contracts module — local types and constants.
// Phase 1 (2026-05-24). DB tables: contracts, contract_line_items.
// ============================================================================

export type ContractType =
  | 'New_Account'
  | 'Account_Expansion'
  | 'Added_Position'
  | 'Project_Addendum'
  | 'Rate_Change'
  | 'Renewal';

export type BillingModel = 'Monthly' | 'Hourly' | 'Project_Only' | 'Hybrid';

export type ContractStatus =
  | 'Draft'
  | 'Pending_Finance'
  | 'Pending_Legal'
  | 'Pending_Executive'
  | 'Ready_for_Signature'
  | 'Sent_for_Signature'
  | 'Signed'
  | 'Active'
  | 'Expired'
  | 'Cancelled'
  | 'Rejected';

export type LineType =
  | 'Recurring_Monthly'
  | 'Hourly_Position'
  | 'One_Time_Project'
  | 'Addendum';

export const CONTRACT_TYPES: ContractType[] = [
  'New_Account',
  'Account_Expansion',
  'Added_Position',
  'Project_Addendum',
  'Rate_Change',
  'Renewal',
];

export const CONTRACT_TYPE_LABELS: Record<ContractType, string> = {
  New_Account: 'New Account',
  Account_Expansion: 'Account Expansion',
  Added_Position: 'Added Position',
  Project_Addendum: 'Project Addendum',
  Rate_Change: 'Rate Change',
  Renewal: 'Renewal',
};

export const BILLING_MODELS: BillingModel[] = [
  'Monthly',
  'Hourly',
  'Project_Only',
  'Hybrid',
];

export const CONTRACT_STATUSES: ContractStatus[] = [
  'Draft',
  'Pending_Finance',
  'Pending_Legal',
  'Pending_Executive',
  'Ready_for_Signature',
  'Sent_for_Signature',
  'Signed',
  'Active',
  'Expired',
  'Cancelled',
  'Rejected',
];

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  Draft: 'Draft',
  Pending_Finance: 'Pending Finance',
  Pending_Legal: 'Pending Legal',
  Pending_Executive: 'Pending Executive',
  Ready_for_Signature: 'Ready for Signature',
  Sent_for_Signature: 'Sent for Signature',
  Signed: 'Signed',
  Active: 'Active',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
  Rejected: 'Rejected',
};

// Tailwind class fragments for status pills.
export const CONTRACT_STATUS_CLASSES: Record<ContractStatus, string> = {
  Draft: 'bg-tcs-surface text-tcs-text-secondary',
  Pending_Finance: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Pending_Legal: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Pending_Executive: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Ready_for_Signature: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Sent_for_Signature: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Signed: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Active: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Expired: 'bg-tcs-border text-tcs-text-secondary',
  Cancelled: 'bg-tcs-border text-tcs-text-secondary',
  Rejected: 'bg-st-danger-bg-strong text-st-danger-fg',
};

export const CONTRACT_TYPE_CLASSES: Record<ContractType, string> = {
  New_Account: 'bg-st-ok-bg text-st-ok-fg border border-st-ok-border',
  Account_Expansion: 'bg-st-info-bg text-st-info-fg border border-st-info-border',
  Added_Position: 'bg-violet-50 text-violet-700 border border-violet-200',
  Project_Addendum: 'bg-st-warn-bg text-st-warn-fg border border-st-warn-border',
  Rate_Change: 'bg-st-warn-bg text-st-warn-fg border border-st-warn-border',
  Renewal: 'bg-tcs-bg-muted text-tcs-text-secondary border border-tcs-border',
};

export const BILLING_MODEL_CLASSES: Record<BillingModel, string> = {
  Monthly: 'bg-tcs-surface text-tcs-text-secondary',
  Hourly: 'bg-tcs-surface text-tcs-text-secondary',
  Project_Only: 'bg-tcs-surface text-tcs-text-secondary',
  Hybrid: 'bg-tcs-surface text-tcs-text-secondary',
};

export const LINE_TYPES: LineType[] = [
  'Recurring_Monthly',
  'Hourly_Position',
  'One_Time_Project',
  'Addendum',
];

export const LINE_TYPE_LABELS: Record<LineType, string> = {
  Recurring_Monthly: 'Recurring Monthly',
  Hourly_Position: 'Hourly Position',
  One_Time_Project: 'One-Time Project',
  Addendum: 'Addendum',
};

// ----- Row shapes (denormalized from PostgREST embedded selects) ------------

export interface ContractRow {
  contract_id: string;
  contract_number: string | null;
  account_id: string;
  facility_id: string | null;
  source_bid_id: string | null;
  source_opportunity_id: string | null;
  contract_type: ContractType;
  billing_model: BillingModel;
  monthly_rate: number | null;
  hourly_rate: number | null;
  estimated_monthly_hours: number | null;
  start_date: string | null;
  end_date: string | null;
  scope_summary: string | null;
  scope_of_work: string | null;
  payment_terms: string | null;
  notes: string | null;
  status: ContractStatus;
  created_by_eid: string | null;
  finance_approved_by_eid: string | null;
  finance_approved_at: string | null;
  finance_approval_notes: string | null;
  legal_approved_by_eid: string | null;
  legal_approved_at: string | null;
  legal_approval_notes: string | null;
  executive_approved_by_eid: string | null;
  executive_approved_at: string | null;
  executive_approval_notes: string | null;
  signed_at: string | null;
  document_url: string | null;
  // E-sign evidence layer (Phase 1, 20260603) + signer identity (Phase 2)
  document_hash: string | null;
  hash_algo: string | null;
  certificate_url: string | null;
  sealed_pdf_url: string | null;
  signature_method: string | null;
  signer_name: string | null;
  signer_email: string | null;
  signer_title: string | null;
  rejection_reason: string | null;
  rejected_at: string | null;
  rejected_by_eid: string | null;
  // Phase 2 (20260610): amendment chain + template authoring provenance
  parent_contract_id: string | null;
  authored_from_template_id: string | null;
  authored_pdf_generated_at: string | null;
  created_at: string;
  updated_at: string;
  // Embedded joins
  accounts?: { account_name: string | null } | null;
  facilities?: { facility_name: string | null; market_region: string | null } | null;
  creator?: { employee_name_first: string | null; employee_name_last: string | null } | null;
  // Client-side enrichment (list view): covered-facility count for
  // account-wide contracts, from contract_facilities.
  covered_facility_count?: number;
}

// Phase 2 — multi-facility junction row (contract_facilities).
export interface ContractFacilityRow {
  contract_facility_id: string;
  contract_id: string;
  facility_id: string;
  monthly_rate: number | null;
  hourly_rate: number | null;
  notes: string | null;
  created_at: string;
  facilities?: { facility_name: string | null } | null;
}

// Phase 2 — amendment types that may reference a parent contract.
export const AMENDMENT_TYPES: ContractType[] = [
  'Rate_Change',
  'Account_Expansion',
  'Renewal',
];

// Phase 2 — lite shape for children/parent-picker queries.
export interface ContractLite {
  contract_id: string;
  contract_number: string | null;
  contract_type: ContractType;
  status: ContractStatus;
  start_date: string | null;
}

export interface ContractLineItemRow {
  line_id: string;
  contract_id: string;
  description: string;
  line_type: LineType;
  quantity: number | null;
  unit_label: string | null;
  unit_rate: number | null;
  line_total: number | null;
  sub_cost: number | null;
  margin_pct: number | null;
  notes: string | null;
  created_at: string;
}

// ----- Create / update inputs ----------------------------------------------

export interface NewContractInput {
  account_id: string;
  facility_id: string | null;
  contract_type: ContractType;
  billing_model: BillingModel;
  monthly_rate: number | null;
  hourly_rate: number | null;
  estimated_monthly_hours: number | null;
  start_date: string | null;
  end_date: string | null;
  scope_summary: string | null;
  scope_of_work: string | null;
  payment_terms: string | null;
  notes: string | null;
  source_bid_id: string | null;
  source_opportunity_id: string | null;
  parent_contract_id?: string | null;
}

export type UpdateContractInput = Partial<NewContractInput>;

export interface NewLineItemInput {
  contract_id: string;
  description: string;
  line_type: LineType;
  quantity: number | null;
  unit_label: string | null;
  unit_rate: number | null;
  line_total: number | null;
  sub_cost: number | null;
  notes: string | null;
}

export type UpdateLineItemInput = Partial<Omit<NewLineItemInput, 'contract_id'>>;
